/**
 * QuickFilters - Filtros rápidos para o Kanban.
 * Refatorado: Usa hook utilitário useClickOutside.
 */
import React, { useState, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { AppIcon, IconName } from '../../icons/AppIcon';
import { useClickOutside } from '../../../hooks/useClickOutside';
import { usePermissions } from '../../../hooks/usePermissions';
import { cn } from '@/lib/utils';

export interface QuickFiltersState {
    noAgent: boolean;
    noTasks: boolean;
    overdueTasks: boolean;
    statusWon: boolean;
    statusLost: boolean;
}

interface QuickFiltersProps {
    filters: QuickFiltersState;
    onChange: (filters: QuickFiltersState) => void;
    triggerClassName?: string;
    /** Sem borda própria — para faixa unificada no mobile */
    embedded?: boolean;
}

const FILTER_OPTIONS = [
    { key: 'noAgent', label: 'Sem atendente', icon: 'user-x' },
    { key: 'noTasks', label: 'Sem tarefa', icon: 'clipboard-x' },
    { key: 'overdueTasks', label: 'Tarefa atrasada', icon: 'alarm-clock' },
    { key: 'statusWon', label: 'Ganho', icon: 'check-circle' },
    { key: 'statusLost', label: 'Perda', icon: 'x-circle' },
] as const;

export const QuickFilters: React.FC<QuickFiltersProps> = ({
    filters,
    onChange,
    triggerClassName,
    embedded = false,
}) => {
    const { canFilterByAgent } = usePermissions();
    const [isOpen, setIsOpen] = useState(false);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});

    // Hook utilitário
    useClickOutside(dropdownRef, () => setIsOpen(false), isOpen, [buttonRef]);

    const safeFilters = filters || {
        noAgent: false,
        noTasks: false,
        overdueTasks: false,
        statusWon: false,
        statusLost: false
    };

    const visibleOptions = useMemo(
        () => FILTER_OPTIONS.filter((option) => canFilterByAgent || option.key !== 'noAgent'),
        [canFilterByAgent],
    );

    const activeCount = visibleOptions.filter((option) => safeFilters[option.key]).length;
    const hasSelection = activeCount > 0;

    const calculatePosition = () => {
        if (!buttonRef.current) return;

        const rect = buttonRef.current.getBoundingClientRect();
        const dropdownWidth = 220;
        const viewportWidth = window.innerWidth;

        let left = rect.left;
        if (rect.left + dropdownWidth > viewportWidth - 16) {
            left = rect.right - dropdownWidth;
        }

        setDropdownStyle({
            position: 'fixed',
            top: `${rect.bottom + 4}px`,
            left: `${left}px`,
            zIndex: 9999
        });
    };

    const toggleDropdown = () => {
        if (!isOpen) {
            calculatePosition();
        }
        setIsOpen(!isOpen);
    };

    const toggleFilter = (key: keyof QuickFiltersState) => {
        onChange({
            ...safeFilters,
            [key]: !safeFilters[key]
        });
    };

    const clearAll = () => {
        onChange({
            noAgent: false,
            noTasks: false,
            overdueTasks: false,
            statusWon: false,
            statusLost: false
        });
    };

    return (
        <>
            <button
                ref={buttonRef}
                onClick={toggleDropdown}
                className={cn(
                    'relative group flex shrink-0 items-center justify-center transition-colors',
                    embedded
                        ? 'h-11 w-full min-w-0 rounded-none border-0'
                        : 'h-9 w-9 rounded-lg border bg-card',
                    hasSelection
                        ? embedded
                            ? 'bg-primary-subtle text-primary'
                            : 'border-primary-light bg-primary-subtle text-primary'
                        : embedded
                            ? 'bg-transparent text-secondary hover:bg-surface-subtle hover:text-primary'
                            : 'border-border text-secondary hover:bg-surface-subtle hover:text-primary',
                    triggerClassName,
                )}
                title="Filtros rápidos"
                aria-label="Filtros rápidos"
            >
                <AppIcon
                    name="sliders"
                    className="h-5 w-5 transition-colors"
                />
                {hasSelection && (
                    <span
                        className={cn(
                            'absolute flex items-center justify-center rounded-full bg-primary font-bold text-white',
                            embedded
                                ? 'right-1 top-1 size-3.5 text-[9px] leading-none'
                                : '-right-1.5 -top-1.5 size-4 text-[10px]',
                        )}
                    >
                        {activeCount}
                    </span>
                )}
            </button>

            {isOpen && createPortal(
                <div
                    ref={dropdownRef}
                    style={dropdownStyle}
                    className="w-[220px] bg-background rounded-lg shadow-xl border border-border py-2 z-dropdown"
                >
                    {/* Header */}
                    <div className="px-3 pb-2 border-b border-border flex items-center justify-between">
                        <span className="text-xs font-semibold text-secondary uppercase">Filtro rápido</span>
                        {hasSelection && (
                            <button
                                onClick={clearAll}
                                className="text-xs text-primary hover:text-primary-dark font-medium"
                            >
                                Limpar
                            </button>
                        )}
                    </div>

                    {/* Lista de filtros */}
                    <div className="py-1">
                        {visibleOptions.map(option => (
                            <button
                                key={option.key}
                                onClick={() => toggleFilter(option.key)}
                                className="w-full px-3 py-2.5 text-left text-sm hover:bg-surface-subtle transition-colors flex items-center gap-3"
                            >
                                <div
                                    className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${safeFilters[option.key]
                                        ? 'bg-primary border-primary'
                                        : 'border-border'
                                        }`}
                                >
                                    {safeFilters[option.key] && (
                                        <AppIcon name="check" className="w-3 h-3 text-white" />
                                    )}
                                </div>
                                <span className={safeFilters[option.key] ? 'font-medium text-default' : 'text-secondary'}>
                                    {option.label}
                                </span>
                            </button>
                        ))}
                    </div>
                </div>,
                document.body
            )}
        </>
    );
};

export default QuickFilters;
